import React, {useState, useEffect, useRef} from "react";
import {Card, CardBody, Col, Container, Input, Row} from "reactstrap";
import ApiService from "../util/ApiService";
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from "chart.js";
import {Doughnut, Bar} from "react-chartjs-2";
import "./assets/css/DashboardScale.css";
import PendingConflict from "./pendingConflict";
import {PieChartOutlined, BarChartOutlined, UserOutlined} from "@ant-design/icons";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

document.title = "Escala Padrão - DashBoard | Plantão Rápido";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import locale from "antd/es/date-picker/locale/pt_BR";
import dayjs from "dayjs";
import HospitalSearch from "../hospital/HospitalSearch";
import UsersSearch from "../dashboard/calender/UsersSearch";
import {DatePicker} from "antd";
import AuthService from "../login/AuthService";
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);
const {RangePicker} = DatePicker;
const DoughnutChart = ({title, data}) => (
    <div className="col-12 col-md-6 col-lg-4 mb-3">
        <div className="card h-100 p-3">
            <div className="header-section">
                <h6 className="card-title"><PieChartOutlined/> {title}</h6>
            </div>
            <Doughnut data={data}/>
        </div>
    </div>
);

const BarChart = ({ title, data, col, buttonExport , height = null }) => {
    // Filtrar dados com base em colunas com valor > 0
    const hasValidData = data?.datasets?.some(dataset =>
        dataset.data?.some(value => value > 0)
    );

    // Remover colunas onde todos os valores são zero ou vazios
    const filteredData = React.useMemo(() => {
        if (!data || !data.labels || !data.datasets || data.datasets.length === 0) return null;

        const validIndexes = data.labels.map((_, idx) => {
            return data.datasets.some(dataset => dataset.data[idx] > 0);
        });

        const newLabels = data.labels.filter((_, idx) => validIndexes[idx]);
        const newDatasets = data.datasets.map(dataset => ({
            ...dataset,
            data: dataset.data.filter((_, idx) => validIndexes[idx])
        }));

        return {
            ...data,
            labels: newLabels,
            datasets: newDatasets
        };
    }, [data]);

    return (
        <div className={col}>
            <div className="card w-100 p-3">
                <div className="header-section d-flex justify-content-between align-items-center">
                    <h6 className="card-title mb-0"><BarChartOutlined /> {title}</h6>
                    {buttonExport && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => buttonExport.onClick(buttonExport.event)}
                            disabled={buttonExport.loading}
                        >
                            {buttonExport.loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                                    Exportando...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-download me-1"></i> Exportar
                                </>
                            )}
                        </button>
                    )}
                </div>

                {hasValidData && filteredData && filteredData.labels.length > 0 ? (
                    <Bar height={height} data={filteredData} />
                ) : (
                    <div className="text-center py-5 text-muted">Sem dados disponíveis para exibir.</div>
                )}
            </div>
        </div>
    );
};

const AllocationTable = ({title, allocations, textQtde = 'Plantões'}) => (
    <div className="col-12 col-md-6 col-lg-6 mb-4">
        <div className="card h-100 p-3">
            <div className="header-section">
                <h6 className="card-title"><UserOutlined/> {title}</h6>
            </div>
            <table className="table">
                <thead>
                <tr>
                    <th>Plantonista</th>
                    <th> {textQtde}</th>
                </tr>
                </thead>
                <tbody>
                {allocations.map((item, index) => (
                    <tr key={index}>
                        <td>{item.name}</td>
                        <td>{item.count}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    </div>
);

const ScaleDashboardPage = () => {
    const [exportLoading, setExportLoading] = useState(false);
    const user = AuthService.getUserData();
    const refUserSearch = useRef(null);
    const hospitalSearchRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [barData, setBarData] = useState(null);
    const [hospitalData, setHospitalData] = useState(null);
    const [departmentData, setDepartmentData] = useState(null);
    const [mostAllocated, setMostAllocated] = useState([]);
    const [leastAllocated, setLeastAllocated] = useState([]);
    const [mostRepasses, setMostRepasses] = useState([]);

    const today = dayjs();
    const endOfNextMonth = dayjs().add(1, 'month').endOf('month');
    const [byDayData, setByDayData] = useState(null);
    const [filters, setFilters] = useState({
        dateRange: [today, endOfNextMonth],
        datestart: today.format("YYYY-MM-DD"),
        dateend: endOfNextMonth.format("YYYY-MM-DD"),
        hospital: "",
    });

    const handleFilterChange = (changedFilters) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            ...changedFilters,
        }));
    };


    const onHospitalSelect = (hospital) => {
        if (hospital && hospital.value) {
            // Somente atualiza o filtro se o hospital tiver um ID válido
            handleFilterChange({hospital: hospital.value});
        } else {
            // Se nenhum hospital for selecionado (hospital é null), limpe o filtro
            handleFilterChange({hospital: null});
        }
    };

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const response = await ApiService.send("post", `${process.env.REACT_APP_URL_API}scale/dashboard`, {
                hospital: filters.hospital,
                datestart:  filters?.datestart,
                dateend:  filters?.dateend,
                user: filters.user_id,
                ...(user.users_type_id === 1 && { logged_in_user: true }),}, true);


            const {data} = ApiService.handleApiResponse(response);

            setBarData({
                labels: data.by_day_of_week.map(item => item.name),
                datasets: [{
                    label: "Alocações por Dia",
                    data: data.by_day_of_week.map(item => item.count),
                    backgroundColor: "#36A2EB",
                }],
            });

            setHospitalData({
                labels: data.by_hospital.map(item => item.name.slice(0, 10)),
                datasets: [{
                    label: "Alocações por Hospital",
                    data: data.by_hospital.map(item => item.count),
                    backgroundColor: "#FF6384",
                }],
            });

            setDepartmentData({
                labels: data.by_department.map(item => item.name.slice(0, 10)),
                datasets: [{
                    label: "Alocações por Departamento",
                    data: data.by_department.map(item => item.count),
                    backgroundColor: "#4BC0C0",
                }],
            });

            setMostRepasses(data.top_repasses.map(user => ({
                name: user.name,
                count: user.count
            })));

            setMostAllocated(data.top_users.map(user => ({
                name: user.name,
                count: user.count
            })));

            setLeastAllocated(data.least_users.map(user => ({
                name: user.name,
                count: user.count
            })));

            setByDayData({
                labels: data.by_day.map((item) => item.date),
                datasets: [
                    {
                        label: "Realizados",
                        data: data.by_day.map((item) => item.completed),
                        backgroundColor: "#28a745",
                    },
                    {
                        label: "Repassados",
                        data: data.by_day.map((item) => item.transferred),
                        backgroundColor: "#ffc107",
                    },
                ],
            });

        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);


    const handleSearchFiltersChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            dateRange: "",
            datestart: null,
            dateend: null,
            status: "",
            hospital: "",
        });


        if (hospitalSearchRef.current) {
            hospitalSearchRef.current.clearHospital();
        }
        if (refUserSearch.current) {
            refUserSearch.current.clearUser();
        }
    };

    const buttonExportRepasse = {
        loading: exportLoading,
        event: {
            hospital: filters.hospital,
            datestart: filters.datestart,
            dateend: filters.dateend,
            user: filters.user_id,
        },
        onClick: async (event) => {
            try {


                const responseType = 'blob' ;
                setExportLoading(true);
                const response = await ApiService.send(
                    "post",
                    `${process.env.REACT_APP_URL_API}scale/dashboard/export-by-day`,
                    event,
                    true,
                    responseType
                );

                const fileType =  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

                const fileExtension =  'xlsx';

                // Cria o blob do arquivo
                const blob = new Blob([response], {type: fileType});
                const downloadUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `duties_export_${new Date().toISOString().split('T')[0]}.${fileExtension}`;
                link.click();
                URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                ApiService.handleApiError(error);
            } finally {
                setExportLoading(false);
            }
        },
    };

    return (
        <>

            <React.Fragment>
                <div className="page-content">
                    <Container fluid>
                        <BreadCrumb title="Dashboard" pageTitle="Escala Padrão"/>
                        <Row className="g-4 mb-4">
                            <div className="row mt-1">
                                <div className="col-md-4">
                                    <div className="form-group ">
                                        <label>Data</label>
                                        <div className="form-control">
                                            <RangePicker
                                                style={{border: "none", boxShadow: "none"}}
                                                locale={locale}
                                                format="DD/MM/YYYY"
                                                className="range-picker-custom"
                                                value={filters.dateRange.length > 0 ? filters.dateRange : null}
                                                onChange={(dates) => {
                                                    if (dates) {
                                                        handleSearchFiltersChange("dateRange", dates);
                                                        handleSearchFiltersChange("datestart", dayjs(dates[0]).format("YYYY-MM-DD"));
                                                        handleSearchFiltersChange("dateend", dayjs(dates[1]).format("YYYY-MM-DD"));
                                                    } else {
                                                        handleSearchFiltersChange("dateRange", []);
                                                        handleSearchFiltersChange("datestart", null);
                                                        handleSearchFiltersChange("dateend", null);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <HospitalSearch
                                        ref={hospitalSearchRef}
                                        buttonAdd={false}
                                        cache={false}
                                        validateManager={true}
                                        onHospitalSelect={onHospitalSelect}
                                    />
                                </div>
                                <div className="col-md-4 form-group">
                                    <UsersSearch
                                        ref={refUserSearch}
                                        onUserSelect={(user) =>
                                            setFilters((prevFilters) => ({
                                                ...prevFilters,
                                                user_id: user?.value || "",
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-12 d-flex justify-content-end">
                                    <button type="button" className="btn  mr-1  me-2"
                                            onClick={() => handleClearFilters()}>
                                        <i className="fa fa-eraser"></i> Limpar Filtros
                                    </button>


                                    <button type="button"
                                            className="btn btn-success"
                                            onClick={() => fetchDashboardData()}
                                            disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                Buscando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-search me-2"></i> Buscar
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="container-fluid p-4">

                                {loading ? (
                                    <div className="row">
                                        <div className="col-12 col-md-6 col-lg-4 mb-3">
                                            <Skeleton height={200} count={1} className="mb-3"/>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-4 mb-3">
                                            <Skeleton height={200} count={1} className="mb-3"/>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-4 mb-3">
                                            <Skeleton height={200} count={1} className="mb-3"/>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-6 mb-3">
                                            <Skeleton height={200} count={1}/>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-6 mb-3">
                                            <Skeleton height={200} count={1}/>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="row">
                                            {barData && <BarChart col="col-12 col-md-6 col-lg-4 mb-3" title="Alocações por Dia" data={barData}/>}
                                            {hospitalData &&
                                                <BarChart col="col-12 col-md-6 col-lg-4 mb-3" title="Alocações por Hospital" data={hospitalData}/>}
                                            {departmentData &&
                                                <BarChart col="col-12 col-md-6 col-lg-4 mb-3" title="Alocações por Departamento" data={departmentData}/>}
                                        </div>
                                        <div className="row">
                                            {byDayData &&
                                                <BarChart height={100} col="col-12 col-md-12 col-lg-12 mb-3 " title="Realizados vs Repassados por Dia" data={byDayData}   buttonExport={buttonExportRepasse}/>}


                                        </div>
                                        <div className="row">
                                            <PendingConflict/>
                                        </div>

                                        <div className="row">
                                            <AllocationTable title="Plantonistas Mais Alocados"
                                                             allocations={mostAllocated}/>
                                            <AllocationTable title="Plantonistas Menos Alocados"
                                                             allocations={leastAllocated}/>
                                            <AllocationTable title="Top 5 Plantonistas com Mais Repasses" textQtde="Repasses"
                                                             allocations={mostRepasses}/>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Row>
                    </Container>
                </div>
            </React.Fragment>
        </>
    )
}


export default ScaleDashboardPage;
